import { inject, computed } from "@angular/core";
import { signalStore, withState, withMethods, patchState, withComputed } from "@ngrx/signals";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { firstValueFrom, interval, Subscription, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { KEYCLOAK_CLIENT_ID } from "../constants";
import { AgenciesService, ProvidersService } from "app/services/api";
import { BUSINESS_UNIT } from "app/constants/index";

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}

interface KeycloakTokenPayload {
  name?: string;
  preferredUsername?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  realm_access?: {
    roles: string[];
  };
  organization?: string[];
}

interface UserInfo {
  displayName: string;
  email: string;
  roles: string[];
  firstName: string;
  lastName: string;
}

export interface UserState {
  accessToken?: string;
  refreshToken?: string;
  isAuthenticated: boolean;
  tokenExpiration?: number;
  userInfo?: UserInfo;
  agencyId?: string;
  primaryDomain?: string;
  businessUnit?: BUSINESS_UNIT;
  providerId?: string;
}

export const initialState: UserState = {
  accessToken: localStorage.getItem("accessToken") ?? undefined,
  refreshToken: localStorage.getItem("refreshToken") ?? undefined,
  isAuthenticated: false,
  tokenExpiration: undefined,
  userInfo: undefined,
  agencyId: localStorage.getItem("agencyId") ?? undefined,
  primaryDomain: localStorage.getItem("primaryDomain") ?? undefined,
  businessUnit: localStorage.getItem("businessUnit") as BUSINESS_UNIT | undefined,
};

export const UserStore = signalStore(
  { providedIn: "root" },
  withState<UserState>(initialState),
  withComputed(store => {
    const userInfo = computed(() => {
      const token = store.accessToken?.();
      if (!token) return undefined;

      try {
        const payload = JSON.parse(atob(token.split(".")[1])) as KeycloakTokenPayload;
        return {
          displayName: payload.name ?? payload.preferredUsername ?? "Unknown User",
          email: payload.email ?? "No Email",
          roles: payload.realm_access?.roles ?? [],
          firstName: payload.given_name ?? "",
          lastName: payload.family_name ?? "",
        } as UserInfo;
      } catch (error) {
        console.error("Error parsing token:", error);
        return undefined;
      }
    });

    return {
      userInfo,
      displayName: computed(() => userInfo()?.displayName ?? "Unknown User"),
      userEmail: computed(() => userInfo()?.email ?? "No Email"),
      userRoles: computed(() => userInfo()?.roles ?? []),
      isSuperAdmin: computed(() => userInfo()?.roles?.includes("superadmin") ?? false),
      currentAgencyId: computed(() => store.agencyId?.() ?? undefined),
      currentBusinessUnit: computed(() => store.businessUnit?.() ?? undefined),
    };
  }),
  withMethods(
    (
      store,
      http = inject(HttpClient),
      router = inject(Router),
      agenciesService = inject(AgenciesService),
      providersService = inject(ProvidersService),
    ) => {
      let refreshSub: Subscription | undefined;
      const REFRESH_INTERVAL = 60000; // 1 minute

      const getKeycloakUrl = (): { baseUrl: string; realm: string } => {
        const env = environment.production ? "" : "-staging";
        return {
          baseUrl: `https://auth${env}.services.coara.xyz`,
          realm: `xdrop${env}`,
        };
      };

      const stopTokenRefresh = (): void => {
        if (refreshSub) {
          refreshSub.unsubscribe();
          refreshSub = undefined;
        }
      };

      const persistTokens = (response: AuthResponse): void => {
        localStorage.setItem("accessToken", response.access_token);
        localStorage.setItem("refreshToken", response.refresh_token);
        localStorage.setItem("tokenExpiration", (Date.now() + response.expires_in * 1000).toString());
      };

      const clearAuthState = (): void => {
        patchState(store, initialState);
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenExpiration");
        localStorage.removeItem("agencyId");
        stopTokenRefresh();
        void router.navigate(["/login"]);
      };

      const logout = async (): Promise<void> => {
        const { baseUrl, realm } = getKeycloakUrl();
        const currentRefreshToken = store.refreshToken?.();

        try {
          if (currentRefreshToken) {
            await firstValueFrom(
              http.post(
                `${baseUrl}/realms/${realm}/protocol/openid-connect/logout`,
                new URLSearchParams({
                  client_id: KEYCLOAK_CLIENT_ID,
                  refresh_token: currentRefreshToken,
                }).toString(),
                {
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                  },
                  withCredentials: true,
                },
              ),
            );
          }
        } catch (error) {
          console.error("Logout error", error);
        } finally {
          clearAuthState();
        }
      };

      const refreshToken = async (): Promise<void> => {
        const { baseUrl, realm } = getKeycloakUrl();
        const currentRefreshToken = store.refreshToken?.();

        if (!currentRefreshToken) {
          console.warn("No refresh token available");
          await logout();
          return;
        }

        try {
          const response = await firstValueFrom(
            http.post<AuthResponse>(
              `${baseUrl}/realms/${realm}/protocol/openid-connect/token`,
              new URLSearchParams({
                grant_type: "refresh_token",
                client_id: KEYCLOAK_CLIENT_ID,
                refresh_token: currentRefreshToken,
              }).toString(),
              {
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                },
                withCredentials: true,
              },
            ),
          );

          const tokenExpiration = Date.now() + response.expires_in * 1000;

          patchState(store, {
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            isAuthenticated: true,
            tokenExpiration,
          });

          persistTokens(response);
        } catch (error) {
          console.error("Token refresh error", error);
          if (error instanceof Error && error.message.includes("400")) {
            console.warn("Refresh token expired. Logging out.");
            await logout();
          }
        }
      };

      const startTokenRefresh = (): void => {
        stopTokenRefresh();
        refreshSub = interval(REFRESH_INTERVAL)
          .pipe(
            tap(() => {
              const expiration = store.tokenExpiration?.();
              if (expiration && Date.now() > expiration - REFRESH_INTERVAL) {
                void refreshToken();
              }
            }),
          )
          .subscribe();
      };

      const initializeAgencyFromToken = async (): Promise<void> => {
        const token = store.accessToken?.();
        if (!token) return;

        try {
          const payload = JSON.parse(atob(token.split(".")[1])) as KeycloakTokenPayload;

          if (!payload.organization?.length) {
            if (payload.realm_access?.roles?.includes("superadmin") || payload.realm_access?.roles?.includes("packer")) {
              localStorage.removeItem("agencyId");
              localStorage.removeItem("businessUnit");
              patchState(store, {
                agencyId: undefined,
                businessUnit: undefined,
              });
              return;
            }
            throw new Error("No agencies found in token");
          }

          let lastError: Error | undefined;
          if (payload.realm_access?.roles?.includes("provider")) {
            for (const providerName of payload.organization) {
              try {
                const provider = await firstValueFrom(providersService.providerControllerGetProviderByName(providerName));
                localStorage.setItem("providerId", provider.id);
                patchState(store, {
                  providerId: provider.id,
                });
                return;
              } catch (error) {
                lastError = error as Error;
                console.warn(`Failed to initialize provider ${providerName}:`, error);
              }
            }
          }

          for (const agencyName of payload.organization) {
            try {
              const agency = await firstValueFrom(agenciesService.agencyControllerFindByReadableName(agencyName));
              localStorage.setItem("agencyId", agency.id);
              localStorage.setItem("businessUnit", agency.businessUnit as BUSINESS_UNIT);
              patchState(store, {
                agencyId: agency.id,
                primaryDomain: agency.primaryDomain,
                businessUnit: agency.businessUnit as BUSINESS_UNIT,
              });
              return;
            } catch (error) {
              lastError = error as Error;
              console.warn(`Failed to initialize agency ${agencyName}:`, error);
            }
          }

          throw lastError ?? new Error("Failed to initialize any agency");
        } catch (error) {
          console.error("Error initializing agency:", error);
          throw error;
        }
      };

      return {
        async login(username: string, password: string): Promise<void> {
          patchState(store, { isAuthenticated: false });
          const { baseUrl, realm } = getKeycloakUrl();

          try {
            const response = await firstValueFrom(
              http.post<AuthResponse>(
                `${baseUrl}/realms/${realm}/protocol/openid-connect/token`,
                new URLSearchParams({
                  grant_type: "password",
                  client_id: KEYCLOAK_CLIENT_ID,
                  username,
                  password,
                  scope: "openid profile email",
                }).toString(),
                {
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                  },
                  withCredentials: true,
                },
              ),
            );

            const tokenExpiration = Date.now() + response.expires_in * 1000;

            patchState(store, {
              accessToken: response.access_token,
              refreshToken: response.refresh_token,
              isAuthenticated: true,
              tokenExpiration,
            });

            persistTokens(response);
            startTokenRefresh();

            await initializeAgencyFromToken();

            void router.navigate(["/"]);
          } catch (error) {
            console.error("Login error", error);
            patchState(store, { isAuthenticated: false });
            throw error;
          }
        },
        logout,
        refreshToken,
        initializeAgencyFromToken,
      };
    },
  ),
);
